import { DropResult } from "@hello-pangea/dnd";
import { useState, useEffect } from "react";

import { Label } from "../VersionTab/types";

import type {
  VersionSelectorVersion,
  VersionSelectorVersionId,
} from "@/components/VersionSelector/types";
import { ToastType } from "@/enums";
import { displayToast, displayErrorToast } from "@/utils/toast";

const useLabelDragAndDrop = (
  versions: VersionSelectorVersion[] | undefined,
  onMoveReleaseLabel: (
    labelId: number,
    labelName: string,
    targetVersionId: VersionSelectorVersionId,
  ) => Promise<unknown>,
) => {
  const [isDragging, setIsDragging] = useState(false);
  const [pendingLabelMoves, setPendingLabelMoves] = useState<
    Record<
      string,
      {
        sourceVersionId: VersionSelectorVersionId;
        targetVersionId: VersionSelectorVersionId;
        label: Label;
      }
    >
  >({});
  const [labelMoveConfirmation, setLabelMoveConfirmation] = useState<{
    isOpen: boolean;
    labelId: number | null;
    labelName: string;
    sourceVersionId: VersionSelectorVersionId | null;
    targetVersionId: VersionSelectorVersionId | null;
  }>({
    isOpen: false,
    labelId: null,
    labelName: "",
    sourceVersionId: null,
    targetVersionId: null,
  });

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = async (result: DropResult) => {
    setIsDragging(false);

    if (!result.destination) {
      return;
    }

    const { draggableId, source, destination } = result;

    if (source.droppableId === destination.droppableId) {
      return;
    }

    const labelName = draggableId.split("-")[2];
    const labelId = Number(draggableId.split("-")[1]);

    const rawSourceId = source.droppableId.replace("version-", "");
    const rawTargetId = destination.droppableId.replace("version-", "");
    const sourceVersionId: VersionSelectorVersionId = /^\d+$/.test(rawSourceId)
      ? Number(rawSourceId)
      : rawSourceId;
    const targetVersionId: VersionSelectorVersionId = /^\d+$/.test(rawTargetId)
      ? Number(rawTargetId)
      : rawTargetId;

    let sourceVersion;

    const pendingMoveKey = Object.keys(pendingLabelMoves).find(key => {
      const move = pendingLabelMoves[key];
      return move.label.id === labelId;
    });

    if (pendingMoveKey) {
      sourceVersion = versions?.find(v => v.id === sourceVersionId);
    } else {
      sourceVersion = versions?.find(v => v.labels.some(l => l.id === labelId));
    }

    const movedLabel =
      sourceVersion?.labels.find(l => l.id === labelId) ||
      (pendingMoveKey ? pendingLabelMoves[pendingMoveKey].label : null);

    if (!movedLabel) {
      return;
    }

    setLabelMoveConfirmation({
      isOpen: true,
      labelId,
      labelName,
      sourceVersionId,
      targetVersionId,
    });
  };

  const executeReleaseMove = async (): Promise<void> => {
    if (
      !labelMoveConfirmation.labelId ||
      !labelMoveConfirmation.targetVersionId ||
      !labelMoveConfirmation.sourceVersionId
    ) {
      return;
    }

    const { labelId, labelName, targetVersionId, sourceVersionId } = labelMoveConfirmation;

    const labelMoveKey = `${labelId}-${targetVersionId}`;

    let sourceVersion;
    const pendingMoveKey = Object.keys(pendingLabelMoves).find(key => {
      const move = pendingLabelMoves[key];
      return move.label.id === labelId;
    });

    if (pendingMoveKey) {
      sourceVersion = versions?.find(v => v.id === sourceVersionId);

      setPendingLabelMoves(prev => {
        const updated = { ...prev };
        delete updated[pendingMoveKey];
        return updated;
      });
    } else {
      sourceVersion = versions?.find(v => v.labels.some(l => l.id === labelId));
    }

    const movedLabel =
      sourceVersion?.labels.find(l => l.id === labelId) ||
      (pendingMoveKey ? pendingLabelMoves[pendingMoveKey].label : null);

    if (!movedLabel) {
      return;
    }

    setPendingLabelMoves(prev => ({
      ...prev,
      [labelMoveKey]: {
        sourceVersionId,
        targetVersionId,
        label: movedLabel,
      },
    }));

    setLabelMoveConfirmation(prev => ({ ...prev, isOpen: false }));

    try {
      const response = await onMoveReleaseLabel(labelId, labelName, targetVersionId);

      // Check if the response indicates pending approval
      if (response && typeof response === "object" && "approved" in response) {
        if (response.approved === false) {
          // Label move requires approval - remove from optimistic updates
          displayToast("Label move pending approval", ToastType.warning);
          setPendingLabelMoves(prev => {
            const updated = { ...prev };
            delete updated[labelMoveKey];
            return updated;
          });
        } else {
          // Label move was approved automatically
          displayToast("Release label moved successfully", ToastType.success);
        }
      } else {
        // No approval info in response, assume success
        displayToast("Release label moved successfully", ToastType.success);
      }
    } catch (e) {
      let errorMessage = "Failed to move release label";

      if (e instanceof Error) {
        errorMessage = e.message;
      } else if (typeof e === "object" && e !== null && "message" in e) {
        errorMessage = String((e as { message: unknown }).message);
      }

      displayErrorToast(errorMessage);

      setPendingLabelMoves(prev => {
        const updated = { ...prev };
        delete updated[labelMoveKey];
        return updated;
      });
    }
  };

  useEffect(() => {
    if (Object.keys(pendingLabelMoves).length === 0 || !versions) {
      return;
    }

    const updatedPendingMoves = { ...pendingLabelMoves };
    let hasChanges = false;

    Object.entries(pendingLabelMoves).forEach(([labelKey, moveInfo]) => {
      const { targetVersionId, label } = moveInfo;

      const targetVersion = versions.find(v => v.id === targetVersionId);

      if (targetVersion && targetVersion.labels.some(l => l.id === label.id)) {
        delete updatedPendingMoves[labelKey];
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setPendingLabelMoves(updatedPendingMoves);
    }
  }, [versions, pendingLabelMoves]);

  return {
    isDragging,
    labelMoveConfirmation,
    setLabelMoveConfirmation,
    handleDragStart,
    handleDragEnd,
    executeReleaseMove,
    pendingLabelMoves,
  };
};

export default useLabelDragAndDrop;
